import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Blocks, Wrench, Rocket } from 'lucide-react';
import { logEventSafe } from '../../analytics/analytics';
import { PageBackLink, PageHero, PagePanel, PageShell } from '../MarketingPageShell';

const upcomingItems = [
  {
    title: 'Community-built widgets',
    description: 'A place for useful widgets built by Toolip users to show up inside the app without feeling bolted on.',
    icon: <Blocks className="h-5 w-5" />,
  },
  {
    title: 'In-app discovery',
    description: 'Browse what the community makes, find something useful, and add it into your workflow faster.',
    icon: <Rocket className="h-5 w-5" />,
  },
  {
    title: 'Still under construction',
    description: 'The foundation is not ready yet, so this page is here as a preview of what is coming next.',
    icon: <Wrench className="h-5 w-5" />,
  },
];

const CommunityPage = () => {
  const location = useLocation();

  useEffect(() => {
    logEventSafe('page_view', { page_path: location.pathname, page_title: 'Community' });
  }, [location.pathname]);

  return (
    <PageShell>
      <div className="space-y-10 py-4">
        <PageBackLink />

        <div className="mx-auto max-w-5xl space-y-8">
          <PageHero
            eyebrow="Community"
            title="Community widgets are on the way."
            description="This area is under construction and will eventually be where community-built widgets can be discovered and used inside Toolip."
          />

          <PagePanel className="overflow-hidden border-amber-400/20 bg-gradient-to-br from-amber-400/10 via-slate-900/80 to-blue-500/10">
            <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
              <div>
                <div className="inline-flex rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-amber-100">
                  Under Construction
                </div>
                <h2 className="mt-5 text-3xl font-semibold text-white">A future home for shared widgets inside the app</h2>
                <p className="mt-4 leading-8 text-gray-300">
                  The goal is to make Toolip feel more expandable without losing the clean, fast desktop experience it already has.
                  When this launches, it will focus on bringing community-made widgets into the app in a way that still feels curated and easy to use.
                </p>
                <p className="mt-4 leading-8 text-gray-300">
                  For now, this page is just a placeholder while the feature set is being built out.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/updates"
                    className="inline-flex items-center rounded-xl bg-white px-5 py-3 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-200"
                  >
                    Follow updates
                  </Link>
                  <Link
                    to="/feedback"
                    className="inline-flex items-center rounded-xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
                  >
                    Share ideas
                  </Link>
                </div>
              </div>

              <div className="space-y-4">
                {upcomingItems.map((item) => (
                  <div key={item.title} className="rounded-[1.5rem] border border-white/10 bg-black/20 p-5 shadow-xl shadow-black/20">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white">
                      {item.icon}
                    </div>
                    <h3 className="mt-4 text-xl font-semibold text-white">{item.title}</h3>
                    <p className="mt-2 leading-7 text-gray-300">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </PagePanel>
        </div>
      </div>
    </PageShell>
  );
};

export default CommunityPage;
